import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getAuthHeaders } from '../utils/auth';
import { API_ENDPOINTS } from '../config/api';
import * as XLSX from 'xlsx';
import './EmailCampaigns.css';

// ─── Toast helper ────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return <div className={`campaign-toast ${type}`}>{message}</div>;
}

// ─── Main Component ──────────────────────────────────────────────
export default function EmailCampaigns() {
  const navigate = useNavigate();

  // Auth
  const [authChecked, setAuthChecked] = useState(false);

  // Campaigns list
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Campaign list pagination
  const CAMPAIGNS_PER_PAGE = 20;
  const [campaignsPage, setCampaignsPage] = useState(0);
  const [campaignsTotalPages, setCampaignsTotalPages] = useState(1);
  const [campaignsTotalElements, setCampaignsTotalElements] = useState(0);

  // Create / Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null); // null = create
  const [formData, setFormData] = useState({ name: '', subject: '', htmlBody: '', recipientsText: '' });
  const [saving, setSaving] = useState(false);
  const [htmlPreview, setHtmlPreview] = useState(false);

  // File upload for recipients
  const [uploadedRecipients, setUploadedRecipients] = useState([]); // [{email, name}]
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [excludeEmails, setExcludeEmails] = useState(new Set()); // Set of emails to exclude
  const [excludeFileName, setExcludeFileName] = useState('');
  const [recipientInputMode, setRecipientInputMode] = useState('file'); // 'file' | 'manual'
  const recipientFileRef = useRef(null);
  const excludeFileRef = useRef(null);

  // Confirm modals
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'send'|'delete', campaign }
  const [sendingIds, setSendingIds] = useState(new Set()); // campaign IDs currently being sent

  // Analytics panel
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsRecipients, setAnalyticsRecipients] = useState([]);
  const [analyticsEvents, setAnalyticsEvents] = useState([]);
  const [analyticsTab, setAnalyticsTab] = useState('overview'); // overview | recipients | events
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsCampaignId, setAnalyticsCampaignId] = useState(null);

  // Pagination for recipients & events (server-side)
  const RECIPIENTS_PER_PAGE = 50;
  const EVENTS_PER_PAGE = 50;
  const [recipientsPage, setRecipientsPage] = useState(0);
  const [recipientsTotalPages, setRecipientsTotalPages] = useState(1);
  const [recipientsTotalElements, setRecipientsTotalElements] = useState(0);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [eventsPage, setEventsPage] = useState(0);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [eventsTotalElements, setEventsTotalElements] = useState(0);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState('ALL'); // ALL | OPEN | CLICK
  const [eventUrlSearch, setEventUrlSearch] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientStatusFilter, setRecipientStatusFilter] = useState('ALL');
  const [recipientOpensFilter, setRecipientOpensFilter] = useState('ALL'); // ALL | OPENED | NOT_OPENED
  const [recipientClicksFilter, setRecipientClicksFilter] = useState('ALL'); // ALL | CLICKED | NOT_CLICKED
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  // ─── Auth check (same as CurrentUserDetails) ──────────────────
  useEffect(() => {
    if (!isAuthenticated()) {
      const tm = setTimeout(() => {
        if (!isAuthenticated()) navigate('/signup');
        else setAuthChecked(true);
      }, 100);
      return () => clearTimeout(tm);
    }
    setAuthChecked(true);
  }, [navigate]);

  // ─── Fetch campaigns list (paginated) ────────────────────────
  const fetchCampaigns = useCallback(async (page = 0) => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_ENDPOINTS.CAMPAIGNS.LIST}?page=${page}&size=${CAMPAIGNS_PER_PAGE}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (res.status === 403) throw new Error('No access – Admin privileges required');
      if (!res.ok) throw new Error(`API returned status ${res.status}`);
      const data = await res.json();
      // Support both paginated response and legacy array response
      let fetched = [];
      if (data.content && Array.isArray(data.content)) {
        fetched = data.content;
        setCampaigns(fetched);
        setCampaignsPage(data.page ?? 0);
        setCampaignsTotalPages(data.totalPages ?? 1);
        setCampaignsTotalElements(data.totalElements ?? fetched.length);
      } else if (Array.isArray(data)) {
        fetched = data;
        setCampaigns(fetched);
        setCampaignsTotalPages(1);
        setCampaignsTotalElements(fetched.length);
      } else {
        setCampaigns([]);
      }
      // Clear sendingIds for campaigns that are no longer DRAFT/FAILED (sent successfully)
      setSendingIds(prev => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        for (const id of prev) {
          const c = fetched.find(x => x.id === id);
          if (!c || c.status === 'SENDING' || c.status === 'SENT') next.delete(id);
        }
        return next;
      });
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authChecked) fetchCampaigns(campaignsPage);
  }, [authChecked, fetchCampaigns, campaignsPage]);

  // ─── Stats from campaign list ────────────────────────────────
  const totalCampaigns = campaignsTotalElements;
  const totalSent = campaigns.filter(c => c.status === 'SENT').length;
  const totalDraft = campaigns.filter(c => c.status === 'DRAFT').length;
  const avgOpenRate = campaigns.length
    ? (campaigns.reduce((s, c) => s + (c.openRate || 0), 0) / campaigns.length).toFixed(1)
    : '0.0';
  const avgClickRate = campaigns.length
    ? (campaigns.reduce((s, c) => s + (c.clickRate || 0), 0) / campaigns.length).toFixed(1)
    : '0.0';

  // ─── Create / Edit modal ─────────────────────────────────────
  const openCreateModal = () => {
    setEditingCampaign(null);
    setFormData({ name: '', subject: '', htmlBody: '', recipientsText: '' });
    setHtmlPreview(false);
    setUploadedRecipients([]);
    setUploadedFileName('');
    setExcludeEmails(new Set());
    setExcludeFileName('');
    setRecipientInputMode('file');
    setShowModal(true);
  };

  const openEditModal = async (campaign) => {
    // Fetch full campaign details including htmlBody
    try {
      const res = await fetch(API_ENDPOINTS.CAMPAIGNS.DETAIL(campaign.id), {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load campaign details');
      const data = await res.json();
      setEditingCampaign(data);
      setFormData({
        name: data.name || '',
        subject: data.subject || '',
        htmlBody: data.htmlBody || '',
        recipientsText: '',
      });
      setHtmlPreview(false);
      setShowModal(true);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const parseRecipients = (text) => {
    if (!text.trim()) return [];
    return text
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => {
        // Support formats: "email" or "email,name" or "name <email>"
        const angleMatch = line.match(/^(.+?)\s*<(.+?)>$/);
        if (angleMatch) return { name: angleMatch[1].trim(), email: angleMatch[2].trim() };
        const commaMatch = line.split(',');
        if (commaMatch.length >= 2) return { email: commaMatch[0].trim(), name: commaMatch[1].trim() };
        return { email: line.trim(), name: '' };
      });
  };

  // Parse uploaded file (CSV or Excel)
  const parseRecipientsFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const isExcel = /\.(xlsx|xls)$/i.test(file.name);

      reader.onload = (e) => {
        try {
          let data;
          if (isExcel) {
            const wb = XLSX.read(e.target.result, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            data = XLSX.utils.sheet_to_json(ws, { defval: '' });
          } else {
            // CSV parsing
            const text = e.target.result;
            const lines = text.split(/\r?\n/).filter(l => l.trim());
            if (lines.length === 0) { resolve([]); return; }
            // Detect separator (, or ; or \t)
            const sep = lines[0].includes('\t') ? '\t' : lines[0].includes(';') ? ';' : ',';
            const headers = lines[0].split(sep).map(h => h.trim().replace(/^"|"$/g, ''));
            data = lines.slice(1).map(line => {
              const vals = line.split(sep).map(v => v.trim().replace(/^"|"$/g, ''));
              const row = {};
              headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
              return row;
            });
          }

          // Map columns: find email and name columns (case-insensitive, flexible header names)
          const recipients = [];
          for (const row of data) {
            const keys = Object.keys(row);
            let email = '';
            let name = '';
            for (const key of keys) {
              const lk = key.toLowerCase().replace(/[\s_-]/g, '');
              if (['emailid', 'email', 'emailaddress', 'e-mail', 'mail'].includes(lk)) {
                email = String(row[key] || '').trim();
              } else if (['name', 'fullname', 'firstname', 'recipientname'].includes(lk)) {
                name = String(row[key] || '').trim();
              }
            }
            // Fallback: if no header match, try first and second columns
            if (!email && keys.length >= 1) email = String(row[keys[0]] || '').trim();
            if (!name && keys.length >= 2) name = String(row[keys[1]] || '').trim();
            // Basic email validation
            if (email && email.includes('@')) {
              recipients.push({ email, name });
            }
          }
          resolve(recipients);
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));

      if (isExcel) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  // Parse exclude file (same format, only needs email column)
  const parseExcludeFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const isExcel = /\.(xlsx|xls)$/i.test(file.name);

      reader.onload = (e) => {
        try {
          let data;
          if (isExcel) {
            const wb = XLSX.read(e.target.result, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            data = XLSX.utils.sheet_to_json(ws, { defval: '' });
          } else {
            const text = e.target.result;
            const lines = text.split(/\r?\n/).filter(l => l.trim());
            if (lines.length === 0) { resolve(new Set()); return; }
            const sep = lines[0].includes('\t') ? '\t' : lines[0].includes(';') ? ';' : ',';
            const headers = lines[0].split(sep).map(h => h.trim().replace(/^"|"$/g, ''));
            data = lines.slice(1).map(line => {
              const vals = line.split(sep).map(v => v.trim().replace(/^"|"$/g, ''));
              const row = {};
              headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
              return row;
            });
          }

          const emails = new Set();
          for (const row of data) {
            const keys = Object.keys(row);
            let email = '';
            for (const key of keys) {
              const lk = key.toLowerCase().replace(/[\s_-]/g, '');
              if (['emailid', 'email', 'emailaddress', 'e-mail', 'mail'].includes(lk)) {
                email = String(row[key] || '').trim().toLowerCase();
              }
            }
            if (!email && keys.length >= 1) email = String(row[keys[0]] || '').trim().toLowerCase();
            if (email && email.includes('@')) {
              emails.add(email);
            }
          }
          resolve(emails);
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));

      if (isExcel) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  const handleRecipientFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const recipients = await parseRecipientsFile(file);
      if (recipients.length === 0) {
        showToast('No valid email addresses found in the file. Ensure header has "Email id" or "Email" column.', 'error');
        return;
      }
      setUploadedRecipients(recipients);
      setUploadedFileName(file.name);
      showToast(`Loaded ${recipients.length} recipients from ${file.name}`);
    } catch (err) {
      showToast('Failed to parse file: ' + err.message, 'error');
    }
    // Reset file input so same file can be re-uploaded
    if (recipientFileRef.current) recipientFileRef.current.value = '';
  };

  const handleExcludeFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const emails = await parseExcludeFile(file);
      if (emails.size === 0) {
        showToast('No valid email addresses found in the exclude file.', 'error');
        return;
      }
      setExcludeEmails(emails);
      setExcludeFileName(file.name);
      showToast(`Loaded ${emails.size} emails to exclude from ${file.name}`);
    } catch (err) {
      showToast('Failed to parse exclude file: ' + err.message, 'error');
    }
    if (excludeFileRef.current) excludeFileRef.current.value = '';
  };

  // Get final recipients (uploaded minus excluded)
  const getFinalRecipients = () => {
    if (recipientInputMode === 'manual') {
      const parsed = parseRecipients(formData.recipientsText);
      if (excludeEmails.size === 0) return parsed;
      return parsed.filter(r => !excludeEmails.has(r.email.toLowerCase()));
    }
    if (excludeEmails.size === 0) return uploadedRecipients;
    return uploadedRecipients.filter(r => !excludeEmails.has(r.email.toLowerCase()));
  };

  const finalRecipients = getFinalRecipients();
  const recipientCount = finalRecipients.length;
  const excludedCount = recipientInputMode === 'file'
    ? uploadedRecipients.length - finalRecipients.length
    : parseRecipients(formData.recipientsText).length - finalRecipients.length;

  const handleSaveCampaign = async () => {
    if (!formData.name.trim() || !formData.subject.trim() || !formData.htmlBody.trim()) {
      showToast('Name, subject, and HTML body are required', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingCampaign) {
        // Update existing draft
        const res = await fetch(API_ENDPOINTS.CAMPAIGNS.UPDATE(editingCampaign.id), {
          method: 'PUT',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            name: formData.name,
            subject: formData.subject,
            htmlBody: formData.htmlBody,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'Failed to update campaign');
        }
        showToast('Campaign updated successfully');
      } else {
        // Create new
        const recipients = getFinalRecipients();
        if (recipients.length === 0) {
          showToast('At least one recipient is required. Upload a CSV/Excel file or enter manually.', 'error');
          setSaving(false);
          return;
        }
        const res = await fetch(API_ENDPOINTS.CAMPAIGNS.CREATE, {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            name: formData.name,
            subject: formData.subject,
            htmlBody: formData.htmlBody,
            recipients,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'Failed to create campaign');
        }
        showToast('Campaign created successfully');
      }
      setShowModal(false);
      fetchCampaigns(campaignsPage);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Send campaign ───────────────────────────────────────────
  const handleSendCampaign = async (campaign) => {
    setSendingIds(prev => new Set(prev).add(campaign.id));
    setConfirmAction(null);
    try {
      const res = await fetch(API_ENDPOINTS.CAMPAIGNS.SEND(campaign.id), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to send campaign');
      }
      showToast('Campaign sending started!', 'info');
      await fetchCampaigns(campaignsPage);
    } catch (err) {
      showToast(err.message, 'error');
      setSendingIds(prev => { const s = new Set(prev); s.delete(campaign.id); return s; });
    }
  };

  // ─── Delete campaign ─────────────────────────────────────────
  const handleDeleteCampaign = async (campaign) => {
    try {
      const res = await fetch(API_ENDPOINTS.CAMPAIGNS.DELETE(campaign.id), {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to delete campaign');
      }
      showToast('Campaign deleted');
      // Close analytics if it was for this campaign
      if (analyticsData && analyticsData.campaignId === campaign.id) setAnalyticsData(null);
      fetchCampaigns(campaignsPage);
    } catch (err) {
      showToast(err.message, 'error');
    }
    setConfirmAction(null);
  };

  // ─── Analytics ────────────────────────────────────────────────
  const fetchRecipients = useCallback(async (campaignId, page = 0, search = '', status = 'ALL', opensFilter = 'ALL', clicksFilter = 'ALL') => {
    setRecipientsLoading(true);
    try {
      const params = new URLSearchParams({ page, size: RECIPIENTS_PER_PAGE });
      if (search) params.set('search', search);
      if (status && status !== 'ALL') params.set('status', status);
      if (opensFilter === 'OPENED') params.set('hasOpened', 'true');
      else if (opensFilter === 'NOT_OPENED') params.set('hasOpened', 'false');
      if (clicksFilter === 'CLICKED') params.set('hasClicked', 'true');
      else if (clicksFilter === 'NOT_CLICKED') params.set('hasClicked', 'false');
      const res = await fetch(`${API_ENDPOINTS.CAMPAIGNS.RECIPIENTS(campaignId)}?${params}`, {
        headers: getAuthHeaders(), credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to load recipients');
      const data = await res.json();
      if (data.content && Array.isArray(data.content)) {
        setAnalyticsRecipients(data.content);
        setRecipientsPage(data.page ?? 0);
        setRecipientsTotalPages(data.totalPages ?? 1);
        setRecipientsTotalElements(data.totalElements ?? data.content.length);
      } else if (Array.isArray(data)) {
        setAnalyticsRecipients(data);
        setRecipientsTotalPages(1);
        setRecipientsTotalElements(data.length);
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setRecipientsLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async (campaignId, page = 0) => {
    setEventsLoading(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.CAMPAIGNS.EVENTS(campaignId)}?page=${page}&size=${EVENTS_PER_PAGE}`, {
        headers: getAuthHeaders(), credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to load events');
      const data = await res.json();
      if (data.content && Array.isArray(data.content)) {
        setAnalyticsEvents(data.content);
        setEventsPage(data.page ?? 0);
        setEventsTotalPages(data.totalPages ?? 1);
        setEventsTotalElements(data.totalElements ?? data.content.length);
      } else if (Array.isArray(data)) {
        setAnalyticsEvents(data);
        setEventsTotalPages(1);
        setEventsTotalElements(data.length);
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const openAnalytics = async (campaign) => {
    setAnalyticsLoading(true);
    setAnalyticsTab('overview');
    setRecipientsPage(0);
    setEventsPage(0);
    setRecipientSearch('');
    setRecipientStatusFilter('ALL');
    setRecipientOpensFilter('ALL');
    setRecipientClicksFilter('ALL');
    setAnalyticsCampaignId(campaign.id);
    try {
      const [analyticsRes] = await Promise.all([
        fetch(API_ENDPOINTS.CAMPAIGNS.ANALYTICS(campaign.id), { headers: getAuthHeaders(), credentials: 'include' }),
      ]);
      if (!analyticsRes.ok) throw new Error('Failed to load analytics');
      const aData = await analyticsRes.json();
      setAnalyticsData(aData);
      // Fetch first page of recipients and events
      await Promise.all([
        fetchRecipients(campaign.id, 0, '', 'ALL'),
        fetchEvents(campaign.id, 0),
      ]);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Refetch recipients when page/search/filter changes
  const handleRecipientsPageChange = (newPage) => {
    setRecipientsPage(newPage);
    if (analyticsCampaignId) fetchRecipients(analyticsCampaignId, newPage, recipientSearch, recipientStatusFilter, recipientOpensFilter, recipientClicksFilter);
  };

  const handleRecipientSearchChange = (value) => {
    setRecipientSearch(value);
    // Debounce search
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    const timer = setTimeout(() => {
      setRecipientsPage(0);
      if (analyticsCampaignId) fetchRecipients(analyticsCampaignId, 0, value, recipientStatusFilter, recipientOpensFilter, recipientClicksFilter);
    }, 400);
    setSearchDebounceTimer(timer);
  };

  const handleRecipientStatusChange = (value) => {
    setRecipientStatusFilter(value);
    setRecipientsPage(0);
    if (analyticsCampaignId) fetchRecipients(analyticsCampaignId, 0, recipientSearch, value, recipientOpensFilter, recipientClicksFilter);
  };

  const handleRecipientOpensFilterChange = (value) => {
    setRecipientOpensFilter(value);
    setRecipientsPage(0);
    if (analyticsCampaignId) fetchRecipients(analyticsCampaignId, 0, recipientSearch, recipientStatusFilter, value, recipientClicksFilter);
  };

  const handleRecipientClicksFilterChange = (value) => {
    setRecipientClicksFilter(value);
    setRecipientsPage(0);
    if (analyticsCampaignId) fetchRecipients(analyticsCampaignId, 0, recipientSearch, recipientStatusFilter, recipientOpensFilter, value);
  };

  const handleEventsPageChange = (newPage) => {
    setEventsPage(newPage);
    if (analyticsCampaignId) fetchEvents(analyticsCampaignId, newPage);
  };

  // ─── Export to Excel (CSV) — fetches ALL data from server ────
  const exportRecipientsToExcel = async () => {
    if (!analyticsCampaignId) return;
    showToast('Preparing CSV download...', 'info');
    try {
      // Fetch all recipients in batches
      let allRecipients = [];
      let page = 0;
      let totalPages = 1;
      const batchSize = 200;
      while (page < totalPages) {
        const params = new URLSearchParams({ page, size: batchSize });
        if (recipientSearch) params.set('search', recipientSearch);
        if (recipientStatusFilter && recipientStatusFilter !== 'ALL') params.set('status', recipientStatusFilter);
        if (recipientOpensFilter === 'OPENED') params.set('hasOpened', 'true');
        else if (recipientOpensFilter === 'NOT_OPENED') params.set('hasOpened', 'false');
        if (recipientClicksFilter === 'CLICKED') params.set('hasClicked', 'true');
        else if (recipientClicksFilter === 'NOT_CLICKED') params.set('hasClicked', 'false');
        const res = await fetch(`${API_ENDPOINTS.CAMPAIGNS.RECIPIENTS(analyticsCampaignId)}?${params}`, {
          headers: getAuthHeaders(), credentials: 'include'
        });
        if (!res.ok) throw new Error('Failed to fetch recipients for export');
        const data = await res.json();
        if (data.content && Array.isArray(data.content)) {
          allRecipients = allRecipients.concat(data.content);
          totalPages = data.totalPages ?? 1;
        } else if (Array.isArray(data)) {
          allRecipients = data;
          break;
        }
        page++;
      }
      if (allRecipients.length === 0) {
        showToast('No recipients data to export', 'error');
        return;
      }
      const headers = ['#', 'Email', 'Name', 'Status', 'Sent At', 'Opened At', 'Clicked At', 'Opens', 'Clicks', 'Error'];
      const rows = allRecipients.map((r, i) => [
        i + 1,
        `"${(r.email || '').replace(/"/g, '""')}"`,
        `"${(r.name || '—').replace(/"/g, '""')}"`,
        r.status || '',
        r.sentAt || '—',
        r.openedAt || '—',
        r.clickedAt || '—',
        r.openCount ?? 0,
        r.clickCount ?? 0,
        `"${(r.errorMessage || '—').replace(/"/g, '""')}"`
      ]);
      const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${analyticsData?.name || 'campaign'}_recipients_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast(`Exported ${allRecipients.length} recipients to CSV`, 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const exportEventsToExcel = async () => {
    if (!analyticsCampaignId) return;
    showToast('Preparing CSV download...', 'info');
    try {
      let allEvents = [];
      let page = 0;
      let totalPages = 1;
      const batchSize = 200;
      while (page < totalPages) {
        const res = await fetch(`${API_ENDPOINTS.CAMPAIGNS.EVENTS(analyticsCampaignId)}?page=${page}&size=${batchSize}`, {
          headers: getAuthHeaders(), credentials: 'include'
        });
        if (!res.ok) throw new Error('Failed to fetch events for export');
        const data = await res.json();
        if (data.content && Array.isArray(data.content)) {
          allEvents = allEvents.concat(data.content);
          totalPages = data.totalPages ?? 1;
        } else if (Array.isArray(data)) {
          allEvents = data;
          break;
        }
        page++;
      }
      // Apply the same client-side filters (type + search) used in the UI
      const filtered = allEvents.filter(ev => {
        if (eventTypeFilter !== 'ALL' && ev.eventType !== eventTypeFilter) return false;
        if (eventUrlSearch.trim()) {
          const q = eventUrlSearch.toLowerCase();
          const matchUrl = (ev.url || '').toLowerCase().includes(q);
          const matchEmail = (ev.recipientEmail || '').toLowerCase().includes(q);
          if (!matchUrl && !matchEmail) return false;
        }
        return true;
      });
      if (filtered.length === 0) {
        showToast('No events data to export (after filters)', 'error');
        return;
      }
      const headers = ['#', 'Type', 'Email', 'URL', 'User Agent', 'Time'];
      const rows = filtered.map((ev, i) => [
        i + 1,
        ev.eventType || '',
        ev.recipientEmail || '—',
        `"${(ev.url || '—').replace(/"/g, '""')}"`,
        `"${(ev.userAgent || '—').replace(/"/g, '""')}"`,
        ev.createdAt ? new Date(ev.createdAt).toLocaleString() : '—'
      ]);
      const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filterSuffix = eventTypeFilter !== 'ALL' ? `_${eventTypeFilter.toLowerCase()}` : '';
      link.download = `${analyticsData?.name || 'campaign'}_events${filterSuffix}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast(`Exported ${filtered.length} events to CSV`, 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // ─── Render guards ────────────────────────────────────────────
  if (!authChecked || loading) {
    return (
      <div className="email-campaigns-page">
        <div className="email-campaigns-container">
          <div className="campaigns-loading">
            <div className="spinner" />
            Loading email campaigns...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="email-campaigns-page">
        <div className="email-campaigns-container">
          <div className="campaigns-error">
            <h2>Error</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main Render ──────────────────────────────────────────────
  return (
    <div className="email-campaigns-page">
      <div className="email-campaigns-container">
        {/* Toast */}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        {/* Header */}
        <div className="email-campaigns-header">
          <div>
            <h1>📧 Email Campaigns</h1>
            <p className="admin-note">Admin View – Internal Use Only</p>
          </div>
          <button className="btn-create-campaign" onClick={openCreateModal}>
            ➕ New Campaign
          </button>
        </div>

        {/* Stats */}
        <div className="campaigns-stats">
          <div className="campaign-stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-number">{totalCampaigns}</div>
            <div className="stat-label">Total Campaigns</div>
          </div>
          <div className="campaign-stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-number">{totalSent}</div>
            <div className="stat-label">Sent</div>
          </div>
          <div className="campaign-stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-number">{totalDraft}</div>
            <div className="stat-label">Drafts</div>
          </div>
          <div className="campaign-stat-card">
            <div className="stat-icon">👁️</div>
            <div className="stat-number">{avgOpenRate}%</div>
            <div className="stat-label">Avg Open Rate</div>
          </div>
          <div className="campaign-stat-card">
            <div className="stat-icon">🖱️</div>
            <div className="stat-number">{avgClickRate}%</div>
            <div className="stat-label">Avg Click Rate</div>
          </div>
        </div>

        {/* Campaign List */}
        <div className="campaigns-content">
          {campaigns.length === 0 ? (
            <div className="campaigns-empty">
              <div className="empty-icon">📭</div>
              <h3>No campaigns yet</h3>
              <p>Create your first email campaign to get started.</p>
            </div>
          ) : (
            <div className="campaigns-table-wrapper">
              <table className="campaigns-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Recipients</th>
                    <th>Sent</th>
                    <th>Opens</th>
                    <th>Clicks</th>
                    <th>Open %</th>
                    <th>Click %</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c, i) => (
                    <tr key={c.id}>
                      <td>{campaignsPage * CAMPAIGNS_PER_PAGE + i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject}</td>
                      <td>
                        <span className={`status-badge ${c.status?.toLowerCase()}`}>
                          {c.status === 'SENDING' && '⏳ '}
                          {c.status === 'SENT' && '✅ '}
                          {c.status === 'DRAFT' && '📝 '}
                          {c.status === 'FAILED' && '❌ '}
                          {c.status}
                        </span>
                      </td>
                      <td className="metric-cell">{c.totalRecipients ?? 0}</td>
                      <td className="metric-cell">{c.totalSent ?? 0}</td>
                      <td className="metric-cell">{c.totalUniqueOpens ?? c.totalOpens ?? 0}</td>
                      <td className="metric-cell">{c.totalUniqueClicks ?? c.totalClicks ?? 0}</td>
                      <td className="rate-cell">{(c.openRate ?? 0).toFixed(1)}%</td>
                      <td className="rate-cell">{(c.clickRate ?? 0).toFixed(1)}%</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <div className="campaign-actions">
                          {(c.status === 'DRAFT' || c.status === 'FAILED') && (
                            <button
                              className="btn-campaign-action send"
                              title={sendingIds.has(c.id) ? 'Sending in progress...' : 'Send campaign'}
                              disabled={sendingIds.has(c.id)}
                              onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'send', campaign: c }); }}
                            >
                              {sendingIds.has(c.id) ? '⏳ Sending...' : '🚀 Send'}
                            </button>
                          )}
                          {c.status === 'SENDING' && (
                            <button
                              className="btn-campaign-action send"
                              title="Sending in progress..."
                              disabled
                            >
                              ⏳ Sending...
                            </button>
                          )}
                          {c.status === 'DRAFT' && (
                            <button
                              className="btn-campaign-action edit"
                              title="Edit campaign"
                              onClick={(e) => { e.stopPropagation(); openEditModal(c); }}
                            >
                              ✏️ Edit
                            </button>
                          )}
                          <button
                            className="btn-campaign-action analytics"
                            title="View analytics"
                            onClick={(e) => { e.stopPropagation(); openAnalytics(c); }}
                          >
                            📈 Analytics
                          </button>
                          {c.status === 'DRAFT' && (
                            <button
                              className="btn-campaign-action delete"
                              title="Delete campaign"
                              onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'delete', campaign: c }); }}
                            >
                              🗑️ Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Campaign List Pagination */}
              <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    disabled={campaignsPage === 0}
                    onClick={() => setCampaignsPage(0)}
                  >⏮ First</button>
                  <button
                    className="pagination-btn"
                    disabled={campaignsPage === 0}
                    onClick={() => setCampaignsPage(p => Math.max(0, p - 1))}
                  >◀ Prev</button>
                  <span className="pagination-info">
                    Page {campaignsPage + 1} of {campaignsTotalPages} ({campaignsTotalElements} campaigns)
                  </span>
                  <button
                    className="pagination-btn"
                    disabled={campaignsPage >= campaignsTotalPages - 1}
                    onClick={() => setCampaignsPage(p => Math.min(campaignsTotalPages - 1, p + 1))}
                  >Next ▶</button>
                  <button
                    className="pagination-btn"
                    disabled={campaignsPage >= campaignsTotalPages - 1}
                    onClick={() => setCampaignsPage(campaignsTotalPages - 1)}
                  >Last ⏭</button>
                </div>
            </div>
          )}
        </div>

        {/* ─── Analytics Panel ─── */}
        {analyticsLoading && (
          <div className="analytics-panel">
            <div className="campaigns-loading">
              <div className="spinner" />
              Loading analytics...
            </div>
          </div>
        )}

        {analyticsData && !analyticsLoading && (
          <div className="analytics-panel">
            <div className="analytics-panel-header">
              <h2>📈 Analytics: {analyticsData.name}</h2>
              <button className="btn-close-panel" onClick={() => setAnalyticsData(null)}>✕ Close</button>
            </div>

            {/* Tabs */}
            <div className="analytics-tabs">
              <button className={`analytics-tab ${analyticsTab === 'overview' ? 'active' : ''}`} onClick={() => setAnalyticsTab('overview')}>
                Overview
              </button>
              <button className={`analytics-tab ${analyticsTab === 'recipients' ? 'active' : ''}`} onClick={() => setAnalyticsTab('recipients')}>
                Recipients ({recipientsTotalElements})
              </button>
              <button className={`analytics-tab ${analyticsTab === 'events' ? 'active' : ''}`} onClick={() => setAnalyticsTab('events')}>
                Events ({eventsTotalElements})
              </button>
            </div>

            {/* Overview Tab */}
            {analyticsTab === 'overview' && (
              <>
                <div className="analytics-grid">
                  <div className="analytics-card">
                    <div className="a-icon">📬</div>
                    <div className="a-value">{analyticsData.totalRecipients ?? 0}</div>
                    <div className="a-label">Total Recipients</div>
                  </div>
                  <div className="analytics-card">
                    <div className="a-icon">✅</div>
                    <div className="a-value">{analyticsData.totalSent ?? 0}</div>
                    <div className="a-label">Sent</div>
                  </div>
                  <div className="analytics-card">
                    <div className="a-icon">❌</div>
                    <div className="a-value">{analyticsData.totalFailed ?? 0}</div>
                    <div className="a-label">Failed</div>
                  </div>
                  <div className="analytics-card">
                    <div className="a-icon">👁️</div>
                    <div className="a-value">{analyticsData.totalUniqueOpens ?? 0}</div>
                    <div className="a-label">Unique Opens</div>
                  </div>
                  <div className="analytics-card">
                    <div className="a-icon">🖱️</div>
                    <div className="a-value">{analyticsData.totalUniqueClicks ?? 0}</div>
                    <div className="a-label">Unique Clicks</div>
                  </div>
                  <div className="analytics-card">
                    <div className="a-icon">📊</div>
                    <div className="a-value">{(analyticsData.openRate ?? 0).toFixed(1)}%</div>
                    <div className="a-label">Open Rate</div>
                  </div>
                  <div className="analytics-card">
                    <div className="a-icon">🔗</div>
                    <div className="a-value">{(analyticsData.clickRate ?? 0).toFixed(1)}%</div>
                    <div className="a-label">Click Rate</div>
                  </div>
                </div>

                {/* Status breakdown */}
                {analyticsData.recipientStatusBreakdown && (
                  <div className="recipient-breakdown">
                    <div className="breakdown-item">
                      <span className="breakdown-dot sent" />
                      <span className="breakdown-label">Sent</span>
                      <span className="breakdown-count">{analyticsData.recipientStatusBreakdown.sent ?? 0}</span>
                    </div>
                    <div className="breakdown-item">
                      <span className="breakdown-dot pending" />
                      <span className="breakdown-label">Pending</span>
                      <span className="breakdown-count">{analyticsData.recipientStatusBreakdown.pending ?? 0}</span>
                    </div>
                    <div className="breakdown-item">
                      <span className="breakdown-dot failed" />
                      <span className="breakdown-label">Failed</span>
                      <span className="breakdown-count">{analyticsData.recipientStatusBreakdown.failed ?? 0}</span>
                    </div>
                    <div className="breakdown-item">
                      <span className="breakdown-dot bounced" />
                      <span className="breakdown-label">Bounced</span>
                      <span className="breakdown-count">{analyticsData.recipientStatusBreakdown.bounced ?? 0}</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Recipients Tab */}
            {analyticsTab === 'recipients' && (
              <div className="recipients-table-wrapper">
                {/* Toolbar: Search + Filter + Export */}
                <div className="recipients-toolbar">
                  <div className="recipients-toolbar-left">
                    <input
                      className="recipients-search"
                      type="text"
                      placeholder="🔍 Search by email or name..."
                      value={recipientSearch}
                      onChange={(e) => handleRecipientSearchChange(e.target.value)}
                    />
                    <select
                      className="recipients-status-filter"
                      value={recipientStatusFilter}
                      onChange={(e) => handleRecipientStatusChange(e.target.value)}
                    >
                      <option value="ALL">All Status</option>
                      <option value="SENT">Sent</option>
                      <option value="PENDING">Pending</option>
                      <option value="FAILED">Failed</option>
                      <option value="BOUNCED">Bounced</option>
                    </select>
                    <select
                      className="recipients-status-filter"
                      value={recipientOpensFilter}
                      onChange={(e) => handleRecipientOpensFilterChange(e.target.value)}
                    >
                      <option value="ALL">All Opens</option>
                      <option value="OPENED">Opened</option>
                      <option value="NOT_OPENED">Not Opened</option>
                    </select>
                    <select
                      className="recipients-status-filter"
                      value={recipientClicksFilter}
                      onChange={(e) => handleRecipientClicksFilterChange(e.target.value)}
                    >
                      <option value="ALL">All Clicks</option>
                      <option value="CLICKED">Clicked</option>
                      <option value="NOT_CLICKED">Not Clicked</option>
                    </select>
                    <span className="recipients-count-info">
                      {recipientsLoading ? 'Loading...' : `Showing ${analyticsRecipients.length} of ${recipientsTotalElements} recipients`}
                    </span>
                  </div>
                  <button className="btn-export-excel" onClick={exportRecipientsToExcel}>
                    📥 Download All (CSV)
                  </button>
                </div>

                {recipientsLoading ? (
                  <div className="campaigns-loading"><div className="spinner" />Loading recipients...</div>
                ) : analyticsRecipients.length === 0 ? (
                  <div className="campaigns-empty"><p>{recipientSearch || recipientStatusFilter !== 'ALL' || recipientOpensFilter !== 'ALL' || recipientClicksFilter !== 'ALL' ? 'No matching recipients found' : 'No recipients data'}</p></div>
                ) : (
                  <>
                    <table className="recipients-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Email</th>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Sent At</th>
                          <th>Opened At</th>
                          <th>Clicked At</th>
                          <th>Opens</th>
                          <th>Clicks</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsRecipients.map((r, i) => (
                          <tr key={r.id || i}>
                            <td>{recipientsPage * RECIPIENTS_PER_PAGE + i + 1}</td>
                            <td>{r.email}</td>
                            <td>{r.name || '—'}</td>
                            <td>
                              <span className={`status-badge ${r.status?.toLowerCase()}`}>{r.status}</span>
                            </td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{r.sentAt || '—'}</td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{r.openedAt || '—'}</td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{r.clickedAt || '—'}</td>
                            <td className="metric-cell">{r.openCount ?? 0}</td>
                            <td className="metric-cell">{r.clickCount ?? 0}</td>
                            <td style={{ color: r.errorMessage ? '#ef4444' : '#9fb1c8', fontSize: 12 }}>{r.errorMessage || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination Controls */}
                    <div className="pagination-controls">
                        <button
                          className="pagination-btn"
                          disabled={recipientsPage === 0}
                          onClick={() => handleRecipientsPageChange(0)}
                        >⏮ First</button>
                        <button
                          className="pagination-btn"
                          disabled={recipientsPage === 0}
                          onClick={() => handleRecipientsPageChange(Math.max(0, recipientsPage - 1))}
                        >◀ Prev</button>
                        <span className="pagination-info">
                          Page {recipientsPage + 1} of {recipientsTotalPages} ({recipientsTotalElements} total)
                        </span>
                        <button
                          className="pagination-btn"
                          disabled={recipientsPage >= recipientsTotalPages - 1}
                          onClick={() => handleRecipientsPageChange(Math.min(recipientsTotalPages - 1, recipientsPage + 1))}
                        >Next ▶</button>
                        <button
                          className="pagination-btn"
                          disabled={recipientsPage >= recipientsTotalPages - 1}
                          onClick={() => handleRecipientsPageChange(recipientsTotalPages - 1)}
                        >Last ⏭</button>
                      </div>
                  </>
                )}
              </div>
            )}

            {/* Events Tab */}
            {analyticsTab === 'events' && (() => {
              // Client-side filtered events
              const filteredEvents = analyticsEvents.filter(ev => {
                if (eventTypeFilter !== 'ALL' && ev.eventType !== eventTypeFilter) return false;
                if (eventUrlSearch.trim()) {
                  const q = eventUrlSearch.toLowerCase();
                  const matchUrl = (ev.url || '').toLowerCase().includes(q);
                  const matchEmail = (ev.recipientEmail || '').toLowerCase().includes(q);
                  if (!matchUrl && !matchEmail) return false;
                }
                return true;
              });

              return (
              <div className="events-table-wrapper">
                {/* Filters */}
                <div className="events-filter-bar">
                  <div className="events-filter-left">
                    <div className="events-type-filter">
                      <button className={`evt-filter-btn ${eventTypeFilter === 'ALL' ? 'active' : ''}`} onClick={() => setEventTypeFilter('ALL')}>All</button>
                      <button className={`evt-filter-btn ${eventTypeFilter === 'OPEN' ? 'active open' : ''}`} onClick={() => setEventTypeFilter('OPEN')}>👁️ Opens</button>
                      <button className={`evt-filter-btn ${eventTypeFilter === 'CLICK' ? 'active click' : ''}`} onClick={() => setEventTypeFilter('CLICK')}>🔗 Clicks</button>
                    </div>
                    <div className="events-url-search">
                      <input
                        type="text"
                        placeholder="Search by URL or email..."
                        value={eventUrlSearch}
                        onChange={e => setEventUrlSearch(e.target.value)}
                      />
                      {eventUrlSearch && <button className="events-search-clear" onClick={() => setEventUrlSearch('')}>✕</button>}
                    </div>
                  </div>
                  <span className="events-filter-count">{filteredEvents.length} of {analyticsEvents.length} events</span>
                </div>

                {/* Export button for events */}
                <div className="recipients-toolbar">
                  <span className="recipients-count-info">
                    {eventsLoading ? 'Loading...' : `${eventsTotalElements} total events`}
                  </span>
                  <button className="btn-export-excel" onClick={exportEventsToExcel}>
                    📥 Download {eventTypeFilter !== 'ALL' ? `${eventTypeFilter.charAt(0) + eventTypeFilter.slice(1).toLowerCase()}s` : 'All'} (CSV)
                  </button>
                </div>

                {eventsLoading ? (
                  <div className="campaigns-loading"><div className="spinner" />Loading events...</div>
                ) : analyticsEvents.length === 0 ? (
                  <div className="campaigns-empty"><p>No tracking events yet</p></div>
                ) : filteredEvents.length === 0 ? (
                  <div className="campaigns-empty"><p>No events match your filters</p></div>
                ) : (
                  <>
                    <table className="events-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Type</th>
                          <th>Email</th>
                          <th>URL</th>
                          <th>User Agent</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEvents.map((ev, i) => (
                          <tr key={ev.id || i}>
                            <td>{eventsPage * EVENTS_PER_PAGE + i + 1}</td>
                            <td>
                              <span className={`event-type-badge ${ev.eventType?.toLowerCase()}`}>
                                {ev.eventType === 'OPEN' ? '👁️' : '🔗'} {ev.eventType}
                              </span>
                            </td>
                            <td style={{ fontSize: 12 }}>{ev.recipientEmail || '—'}</td>
                            <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ev.url ? <a href={ev.url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>{ev.url}</a> : '—'}
                            </td>
                            <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>
                              {ev.userAgent || '—'}
                            </td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                              {ev.createdAt ? new Date(ev.createdAt).toLocaleString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination Controls */}
                    <div className="pagination-controls">
                        <button
                          className="pagination-btn"
                          disabled={eventsPage === 0}
                          onClick={() => handleEventsPageChange(0)}
                        >⏮ First</button>
                        <button
                          className="pagination-btn"
                          disabled={eventsPage === 0}
                          onClick={() => handleEventsPageChange(Math.max(0, eventsPage - 1))}
                        >◀ Prev</button>
                        <span className="pagination-info">
                          Page {eventsPage + 1} of {eventsTotalPages} ({eventsTotalElements} total)
                        </span>
                        <button
                          className="pagination-btn"
                          disabled={eventsPage >= eventsTotalPages - 1}
                          onClick={() => handleEventsPageChange(Math.min(eventsTotalPages - 1, eventsPage + 1))}
                        >Next ▶</button>
                        <button
                          className="pagination-btn"
                          disabled={eventsPage >= eventsTotalPages - 1}
                          onClick={() => handleEventsPageChange(eventsTotalPages - 1)}
                        >Last ⏭</button>
                      </div>
                  </>
                )}
              </div>
              );
            })()}
          </div>
        )}

        {/* ─── Create / Edit Modal ─── */}
        {showModal && (
          <div className="campaign-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
            <div className="campaign-modal">
              <div className="campaign-modal-header">
                <h2>{editingCampaign ? '✏️ Edit Campaign' : '➕ Create Campaign'}</h2>
                <button className="campaign-modal-close" onClick={() => setShowModal(false)}>✕</button>
              </div>

              <div className="campaign-modal-body">
                <div className="campaign-form-group">
                  <label>Campaign Name <span className="required">*</span></label>
                  <input
                    className="campaign-input"
                    type="text"
                    placeholder="e.g. Product Launch Announcement"
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                  />
                </div>

                <div className="campaign-form-group">
                  <label>Email Subject <span className="required">*</span></label>
                  <input
                    className="campaign-input"
                    type="text"
                    placeholder="e.g. 🚀 Exciting News from BlackKiteAI"
                    value={formData.subject}
                    onChange={(e) => handleFormChange('subject', e.target.value)}
                  />
                </div>

                <div className="campaign-form-group">
                  <label>HTML Body <span className="required">*</span></label>
                  <div className="html-preview-toggle">
                    <button className={!htmlPreview ? 'active' : ''} onClick={() => setHtmlPreview(false)}>Code</button>
                    <button className={htmlPreview ? 'active' : ''} onClick={() => setHtmlPreview(true)}>Preview</button>
                  </div>
                  {htmlPreview ? (
                    <iframe
                      className="html-preview-frame"
                      title="HTML Preview"
                      srcDoc={formData.htmlBody}
                      sandbox=""
                      style={{ height: 300, border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8 }}
                    />
                  ) : (
                    <textarea
                      className="campaign-textarea"
                      placeholder="<html>&#10;  <body>&#10;    <h1>Hello {{name}}</h1>&#10;    <p>Your email content here...</p>&#10;  </body>&#10;</html>"
                      value={formData.htmlBody}
                      onChange={(e) => handleFormChange('htmlBody', e.target.value)}
                    />
                  )}
                </div>

                {/* Recipients – only for new campaigns */}
                {!editingCampaign && (
                  <div className="recipients-section">
                    <label>Recipients <span className="required">*</span></label>

                    {/* Input mode toggle */}
                    <div className="recipient-mode-toggle">
                      <button
                        className={`mode-btn ${recipientInputMode === 'file' ? 'active' : ''}`}
                        onClick={() => setRecipientInputMode('file')}
                        type="button"
                      >
                        📁 Upload File
                      </button>
                      <button
                        className={`mode-btn ${recipientInputMode === 'manual' ? 'active' : ''}`}
                        onClick={() => setRecipientInputMode('manual')}
                        type="button"
                      >
                        ✏️ Manual Entry
                      </button>
                    </div>

                    {recipientInputMode === 'file' ? (
                      <div className="file-upload-section">
                        {/* Upload Recipients File */}
                        <div className="file-upload-box">
                          <div className="file-upload-label">📥 Upload Recipients (CSV / Excel)</div>
                          <p className="file-upload-help">
                            File should have columns: <strong>Email id</strong> (or Email) and <strong>Name</strong> (optional).
                            When Name is present, <code>{`{{name}}`}</code> in the template will be replaced.
                          </p>
                          <div className="file-upload-row">
                            <input
                              ref={recipientFileRef}
                              type="file"
                              accept=".csv,.xlsx,.xls"
                              onChange={handleRecipientFileUpload}
                              className="file-input"
                              id="recipient-file-input"
                            />
                            <label htmlFor="recipient-file-input" className="btn-file-upload">
                              📂 Choose File
                            </label>
                            {uploadedFileName && (
                              <div className="file-upload-info">
                                <span className="file-name">✅ {uploadedFileName}</span>
                                <span className="file-count">{uploadedRecipients.length} emails loaded</span>
                                <button
                                  className="btn-clear-file"
                                  onClick={() => { setUploadedRecipients([]); setUploadedFileName(''); }}
                                  type="button"
                                >✕ Clear</button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Upload Exclude File */}
                        <div className="file-upload-box exclude-box">
                          <div className="file-upload-label">🚫 Exclude List (Optional)</div>
                          <p className="file-upload-help">
                            Upload a CSV/Excel with emails to <strong>exclude</strong> from the recipients list above.
                          </p>
                          <div className="file-upload-row">
                            <input
                              ref={excludeFileRef}
                              type="file"
                              accept=".csv,.xlsx,.xls"
                              onChange={handleExcludeFileUpload}
                              className="file-input"
                              id="exclude-file-input"
                            />
                            <label htmlFor="exclude-file-input" className="btn-file-upload exclude">
                              📂 Choose Exclude File
                            </label>
                            {excludeFileName && (
                              <div className="file-upload-info">
                                <span className="file-name">🚫 {excludeFileName}</span>
                                <span className="file-count">{excludeEmails.size} emails to exclude</span>
                                <button
                                  className="btn-clear-file"
                                  onClick={() => { setExcludeEmails(new Set()); setExcludeFileName(''); }}
                                  type="button"
                                >✕ Clear</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="recipients-help-text">
                          One recipient per line. Formats accepted:<br />
                          <code>email@example.com</code> &nbsp;|&nbsp;
                          <code>email@example.com, John Doe</code> &nbsp;|&nbsp;
                          <code>John Doe &lt;email@example.com&gt;</code>
                        </p>
                        <textarea
                          className="recipients-textarea"
                          placeholder={"alice@example.com, Alice\nbob@example.com, Bob\njane@example.com"}
                          value={formData.recipientsText}
                          onChange={(e) => handleFormChange('recipientsText', e.target.value)}
                        />

                        {/* Exclude file for manual mode too */}
                        <div className="file-upload-box exclude-box" style={{ marginTop: '1rem' }}>
                          <div className="file-upload-label">🚫 Exclude List (Optional)</div>
                          <p className="file-upload-help">
                            Upload a CSV/Excel with emails to exclude from the list above.
                          </p>
                          <div className="file-upload-row">
                            <input
                              ref={excludeFileRef}
                              type="file"
                              accept=".csv,.xlsx,.xls"
                              onChange={handleExcludeFileUpload}
                              className="file-input"
                              id="exclude-file-input-manual"
                            />
                            <label htmlFor="exclude-file-input-manual" className="btn-file-upload exclude">
                              📂 Choose Exclude File
                            </label>
                            {excludeFileName && (
                              <div className="file-upload-info">
                                <span className="file-name">🚫 {excludeFileName}</span>
                                <span className="file-count">{excludeEmails.size} emails to exclude</span>
                                <button
                                  className="btn-clear-file"
                                  onClick={() => { setExcludeEmails(new Set()); setExcludeFileName(''); }}
                                  type="button"
                                >✕ Clear</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Recipients summary */}
                    <div className="recipients-summary">
                      <span className="recipients-count">
                        ✅ {recipientCount} recipient{recipientCount !== 1 ? 's' : ''} to send
                      </span>
                      {excludedCount > 0 && (
                        <span className="excluded-count">
                          🚫 {excludedCount} excluded
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="campaign-modal-footer">
                <button className="btn-modal-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn-modal-save" onClick={handleSaveCampaign} disabled={saving}>
                  {saving ? 'Saving...' : editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Confirm Modal ─── */}
        {confirmAction && (
          <div className="confirm-modal-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmAction(null)}>
            <div className="confirm-modal">
              {confirmAction.type === 'send' ? (
                <>
                  <h3>🚀 Send Campaign?</h3>
                  <p>
                    Are you sure you want to send <strong>"{confirmAction.campaign.name}"</strong> to{' '}
                    <strong>{confirmAction.campaign.totalRecipients}</strong> recipients? This cannot be undone.
                  </p>
                  <div className="confirm-modal-actions">
                    <button className="btn-modal-cancel" onClick={() => setConfirmAction(null)}>Cancel</button>
                    <button className="btn-confirm-success" disabled={sendingIds.has(confirmAction.campaign.id)} onClick={() => handleSendCampaign(confirmAction.campaign)}>
                      {sendingIds.has(confirmAction.campaign.id) ? 'Sending...' : 'Yes, Send Now'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3>🗑️ Delete Campaign?</h3>
                  <p>
                    Are you sure you want to delete <strong>"{confirmAction.campaign.name}"</strong>? This cannot be undone.
                  </p>
                  <div className="confirm-modal-actions">
                    <button className="btn-modal-cancel" onClick={() => setConfirmAction(null)}>Cancel</button>
                    <button className="btn-confirm-danger" onClick={() => handleDeleteCampaign(confirmAction.campaign)}>
                      Yes, Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
